import _ from 'lodash';
import LeafLet, { LatLng, LatLngBounds, LatLngLiteral } from 'leaflet';
import {
  IProjectSpaceType,
  ISpaceType,
  ISpaceTypeCategory,
} from 'spw-redux/modules/space_types/types';
import { INote } from 'spw-redux/modules/notes/types';
import { IPhoto } from 'spw-redux/modules/photo/types';
import { AnchorElementPosition } from 'spw-components/floorplan/floor-plan-map-utils';
import { REQUEST_STATUS } from 'spw-constants';
import { IFloor } from '../projects/types';

export enum MeasurementState {
  SET_POINTS = 'SET_POINTS',
  SET_DISTANCE = 'SET_DISTANCE',
  NONE = 'NONE',
  COMPLETE = 'COMPLETE',
  PRE_REDRAW = 'PRE_REDRAW',
  SUGGESTED_POINTS = 'SUGGESTED_POINTS',
  REDRAW_CONFIRM = 'REDRAW_CONFIRM',
  REQUIRED = 'REQUIRED',
  DELETE = 'DELETE',
  CREATE_SPACE_USF = 'CREATE_SPACE_USF',
  EDIT_SPACE_USF = 'EDIT_SPACE_USF',
  DISPLAY_SHAPES = 'DISPLAY_SHAPES',
  DELETE_SHAPE = 'DELETE_SHAPE',
  INSPECT_FLOOR_USF = 'INSPECT_FLOOR_USF',
  CREATE_FLOOR_USF = 'CREATE_FLOOR_USF',
  EDIT_FLOOR_USF = 'EDIT_FLOOR_USF',
  LOADING = 'LOADING',
  COPYING_MEASURED_FLOOR_USF_STARTED = 'COPYING_MEASURED_FLOOR_USF_STARTED',
  COPYING_MEASURED_FLOOR_USF_TARGET_FLOOR_SELECTED = 'COPYING_MEASURED_FLOOR_USF_TARGET_FLOOR_SELECTED',
}

export enum BulkSelectionState {
  DRAWING = 'DRAWING',
  SELECTION = 'SELECTION',
  COPY = 'COPY',
  ROTATE = 'ROTATE',
  MOVE = 'MOVE',
  SCALE = 'SCALE',
  DELETE = 'DELETE',
}

export enum PreviewState {
  CROPPING = 'CROPPING',
}

export enum ToolbarMode {
  NONE,
  DRAWING_POLYGON,
  DRAWING_RECTANGLE,
  EDITING,
  CUTTING_WITH_RECTANGLE,
  CUTTING_WITH_POLYGON,
  ROTATING,
  DRAGGING,
  DELETING_VERTEX,
  SCALING,
}

export enum TooltipPositionState {
  INSIDE = 'INSIDE',
  OUTSIDE = 'OUTSIDE',
}

export interface IFloorplanState {
  floorplanGeoJsonIsLoading: boolean;
  floorplanGeoJson: GeoJSON.GeoJsonObject;
  currentFloorplan: IFloorplan | null;
  currentFloorplanUpload?: FloorplanUpload;
  isUploadingFloorplan: boolean;
  isUploadingPDF: boolean;
  isArchivingFloorplan: boolean;
  isDownloadingFloorplanPDF: boolean;
  editHistory: EditHistory;
  showCancelUploadDialog: boolean;
  currentControlState: IFloorplanControlsState;
  oldControlState: IFloorplanControlsState | null;
  floorMapState: FloorMapStates;
  spacesMap: Map<string, ISpace[]>;
  floorSpaces: Record<string, ISpace[]>;
  needToBuildLayers: boolean;
  isLoadingSpaces: boolean;
  selectedSpace: ISpace | null;
  spaceToUpdate: ISpace | null;
  floorplanRequestStatus: REQUEST_STATUS;
  isLoadingFloorplanImage: boolean;
  imageData: null | string;
  originalCalibration: ICalibration | null;
  calibration: ICalibration | null;
  suggestedCalibration?: SuggestedCalibration;
  isDrawingCalibrationLine: boolean;
  isMeasuredShapeDirty: boolean;
  previousMapBoundsAndZoom: {
    bounds: L.LatLngBoundsExpression;
    zoom: number;
  };
  measurementState: MeasurementState;
  activeShape: null | SpaceShapeVM;
  floorUsfShapeCopy: {
    sourceFloor: IFloor;
    shape: SpaceShapeVM;
    calibration: ICalibration;
  } | null;
  originalShape?: null | SpaceShapeVM;
  shapes: ISpaceShape[];
  clusters: ICluster[];
  didUpdate: boolean;
  workingLayer?: any;
  bulkSelectionState?: BulkSelectionState | null;
  floorNameWhereObjectsComeFrom?: string;
  selectedSpaceLayers: ISelectedSpaceLayer[];
  bulkOperationSourceFloorCalibration?: ICalibration;
  selectionShapePoints?: LatLngLiteral[][];
  originalImageDimensions: { width: number; height: number };
  originalFileDataUrl: string;
  isExitFloorplanOperationDialogOpen: boolean;
  tooltipPositionState?: TooltipPositionState | null;
  mapMounted: boolean;
  isDownloadingWorksheetSpreadsheet: boolean;
  miniModalState: IFloorplanMiniModalState;
  addNoteDialogState: IFloorplanDialogState;
  addSpaceDialogState: IFloorplanDialogState;
  isDeletingCluster?: boolean;
  toolbarMode: ToolbarMode;
  previewState: PreviewState;
  previewImageBounds: LatLngBounds;
  adjustedFloorplanItems: IAdjustedFloorplanItems;
  changesForBulkOperationArePending?: boolean;
}

export interface IFloorplanDialogState {
  isOpen?: boolean;
  currentMarkerPosition?: IFloorplanPoint;
  anchorEl?: any;
  newSpaceMarker?: any;
}

export interface IFloorplanMiniModalState {
  isUpdateSpaceModalOpen?: boolean;
  isEditCalibrationModalOpen?: boolean;
  handleOnClose?: () => void;
  anchorEl?: any;
  space?: ISpace;
  currentMarkerPosition?: IFloorplanPoint;
  updateSpaceDialogInitialPosition?: AnchorElementPosition;
}

export enum FloorMapStates {
  AddSpaceType = 'AddSpaceType',
  AddFloorNote = 'AddFloorNote',
  EditSpaceType = 'EditSpaceType',
  Show = 'Show',
  Disable = 'Disable',
  SeatCount = 'SeatCount',
  SeatCountMidState = 'SeatCountMidState',
  SpaceType = 'SpaceType',
  Photo = 'Photo',
  Note = 'Note',
  Calibration = 'Calibration',
  ViewSpacesUSF = 'ViewSpacesUSF',
}

export type SpaceTypeCategoryMap = {
  [key: string]: ISpaceTypeCategory;
};

export enum MarkerContentType {
  None,
  NewSpace,
  Photo,
  Note,
  SeatCount,
  Cluster,
  USF,
  Icon,
}

export enum EditAction {
  Crop = 'CROP',
}

export type CropZoneRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export interface EditHistory {
  commands: EditCommand[];
}

export type EditCommand = {
  action: EditAction;
  data: CropZoneRect;
};

class FloorPlanControlsState {
  addSpaceAction = true;

  addNoteAction = true;

  showSpaceCategoriesLayer = true;

  showSeatCountLayer = false;

  showSpaceTypeLayer = false;

  showSpacesUSFLayer = false;

  showPhotosLayer = true;

  showNotesLayer = true;

  grabMapAction = true;

  zoomInAction = true;

  zoomOutAction = true;

  showIndividualWorkspacesAltWorkpoints = true;

  showIndividualWorkSpaces = true;

  showMeetingSpaces = true;

  showMeetingSpacesAltWorkpoints = true;

  showAmenitySpaces = true;

  showAmenitySpacesAltWorkpoints = true;

  showSupportSpaces = true;

  showSupportSpacesAltWorkpoints = true;

  showOtherSpaces = true;

  onlyShowNotes = false;

  onlyShowPhotos = false;

  showSeatCountButton = true;

  showOtherCategoriesLayer = true;

  enableMeasurements = false;

  showIndividualWorkSpacesIntermediate = false;
}

export type IFloorplanControlsState = FloorPlanControlsState;

const keys: Array<string> = Object.keys(new FloorPlanControlsState());

export interface IFloorplan {
  id: number;
  floorId: number;
  s3Path: string;
}

export interface FloorplanUpload {
  id: number;
  geojsonS3Path?: string;
  geojsonStatus?: string;
  geojsonSourceType?: string;
  status: string;
}

export interface ISpaceUsage {
  usage: IUsage;
  seatsInUse?: number;
  completed?: number;
  status?: 'in_use' | 'not_in_use' | 'skipped_or_not_observed';
}

export interface ISpace {
  id?: number;
  floorId?: number;
  floorplanPoint: IFloorplanPoint;
  height?: number;
  name?: string;
  notes?: INote[];
  photos?: IPhoto[];
  roomName: string;
  roomNumber: string;
  seatCount: number;
  spaceType?: ISpaceType;
  allowBlankSeats?: boolean;
  allowBlankUsf?: boolean;
  projectSpaceType?: IProjectSpaceType;
  spaceTypeId?: number;
  projectSpaceTypeId?: number;
  usf?: number;
  width?: number;
  spaceUsage?: ISpaceUsage;
  rank?: string;
  isCluster?: boolean;
  isAlternativeWorkpoint?: boolean;
  alternativeWorkpointSeats?: number;
  shape?: ISpaceShape;
  justCreated?: boolean;
}

export interface SpaceMarker extends ISpace {
  markerOptions: Omit<IMarkerOptions, 'icon'>;
}

export interface ISelectionSpaceShape extends ISpaceShape {
  drawnShape?: L.Polygon;
}

export interface ISelectedSpaceLayer {
  spaces: SpaceMarker[];
  shapes: ISelectionSpaceShape[];
}

export interface IMinimalSpace {
  floorId: number;
  floorplanPoint: IFloorplanPoint;
  spaceTypeId: number;
  projectSpaceTypeId?: number;
  height?: number;
  name?: string;
  roomName?: string;
  roomNumber?: string;
  seatCount?: number;
  spaceType?: ISpaceType;
  usf?: number;
  width?: number;
  allowBlankSeats?: boolean;
  allowBlankUsf?: boolean;
}

export type IMarkerOptions = L.MarkerOptions & {
  content?: MarkerContentType;
  spaceCss: string;
  space: ISpace;
  isSmall?: boolean;
  backgroundColor: string;
  isPartOfCluster: boolean;
  isPartOfMultiSelection: boolean;
  disabled?: boolean;
};

export interface IMarker extends L.Marker {
  // eslint-disable-next-line camelcase
  _leaflet_id: number;
  options: IMarkerOptions;
  space: () => ISpace;
  getIsSmall: () => boolean;
  updateBubble: (updateOptions: {
    backgroundColor?: string;
    content: MarkerContentType;
    isPartOfMultiSelection?: boolean;
    selected?: boolean;
    shouldShowAltWorkpointIndicator?: boolean;
  }) => void;
  setBubble: (
    spaceCss: string,
    content: MarkerContentType,
    backgroundColor: string,
    selected: boolean,
    isPartOfMultiSelection: boolean
  ) => void;
  disable: () => void;
  enable: () => void;
  remove: () => this;
  addTo: (map: LeafLet.Map) => this;
  createNonInteractiveCopy: () => IMarker;
  revertToInitialCoordinates: () => this;
  isInsidePolygon: (polygon: L.Polygon, pointSize?: number) => boolean;
  isSelectable: () => boolean;
  getLatLngBoundingCorners: (map: L.Map) => L.LatLng[];
}

// The code below just goes through all keys in FloorPlanControlsState and maps them to a specific configuration
// this removes the need to manually add any new keys to ALL configurations and keeps things DRY

export const ALL_ENABLED: IFloorplanControlsState = _.reduce(
  keys,
  (obj: any, key: string) => {
    const newObj = obj;
    newObj[key] = true;
    if (_.eq(key, 'enableMeasurements')) {
      newObj[key] = false;
    }

    if (
      _.includes(
        [
          'showSeatCountLayer',
          'showSpaceTypeLayer',
          'showSpacesUSFLayer',
          'onlyShowNotes',
          'onlyShowPhotos',
          'showIndividualWorkSpacesIntermediate',
        ],
        key
      )
    ) {
      newObj[key] = false;
    }
    return newObj;
  },
  {}
);

const allowedSeatCounts = [
  'addSpaceAction',
  'addNoteAction',
  'showSpaceCategoriesLayer',
  'showSeatCountLayer',
  'grabMapAction',
  'zoomInAction',
  'zoomOutAction',
  'showIndividualWorkSpaces',
  'showMeetingSpaces',
  'showAmenitySpaces',
  'showSupportSpaces',
  'showSeatCountButton',
  'showIndividualWorkspacesAltWorkpoints',
  'showMeetingSpacesAltWorkpoints',
  'showAmenitySpacesAltWorkpoints',
  'showSupportSpacesAltWorkpoints',
];

export const SEAT_COUNT_ENABLED: IFloorplanControlsState = _.reduce(
  keys,
  (obj: any, key: string) => {
    const newObj = obj;
    newObj[key] = false;
    if (allowedSeatCounts.indexOf(key) > -1) {
      newObj[key] = true;
    }
    return newObj;
  },
  {}
);

const allowedActionsWhileSpaceTypesFilterActive = [
  'addSpaceAction',
  'addNoteAction',
  'showSpaceCategoriesLayer',
  'showSpaceTypeLayer',
  'grabMapAction',
  'zoomInAction',
  'zoomOutAction',
  'showIndividualWorkSpaces',
  'showMeetingSpaces',
  'showAmenitySpaces',
  'showSupportSpaces',
];

export const SPACE_TYPE_ENABLED: IFloorplanControlsState = _.reduce(
  keys,
  (obj: any, key: string) => {
    const newObj = obj;
    newObj[key] = false;
    if (allowedActionsWhileSpaceTypesFilterActive.indexOf(key) > -1) {
      newObj[key] = true;
    }
    return newObj;
  },
  {}
);

const allowedViewSpacesUSFStates = [
  'showSpaceCategoriesLayer',
  'showSpacesUSFLayer',
  'showIndividualWorkSpaces',
  'showMeetingSpaces',
  'showAmenitySpaces',
  'showSupportSpaces',
  'addSpaceAction',
  'addNoteAction',
];

export const VIEW_SPACES_USF_ENABLED: IFloorplanControlsState = _.reduce(
  keys,
  (obj: any, key: string) => {
    const newObj = obj;
    newObj[key] = false;
    if (allowedViewSpacesUSFStates.indexOf(key) > -1) {
      newObj[key] = true;
    }
    return newObj;
  },
  {}
);

export interface IFloorplanPoint {
  x: number;
  y: number;
}

export type LatLngRing = LatLng[];

export type LatLngPolygon = LatLng[][];

export type LatLngMultiPolygon = LatLng[][][];

export interface ISpaceShape {
  id?: number;
  points?: LatLngPolygon;
  polygons: LatLngMultiPolygon;
  spaceId?: number;
  isFloorUsf?: boolean;
  floorplanId?: number;
  // needed to associate copied spaces to its shape
  markerId?: number;
  // flag to avoid erasing shape during rerendering
  isNewlyCreatedShape?: boolean;
  space?: ISpace;
}

export interface SpaceShapeVM extends ISpaceShape {
  isConfirmed?: boolean;
}

export interface IFloorplanLayers {
  individualSpacesLayer: IMarker[];
  meetingSpacesLayer: IMarker[];
  amenitySpacesLayer: IMarker[];
  supportSpacesLayer: IMarker[];
  customSpacesLayer: IMarker[];
  mediaMarkers: IMarker[];
  calibrationMarkers: IMarker[];
}

export interface IUnattachedNote {
  spaceName: string;
  text: string;
  position: IFloorplanPoint;
  floorId: number;
}

export enum IUsage {
  AWAY_FROM_DESK = 'AWAY_FROM_DESK',
  MEET_WITH_OTHERS = 'MEET_WITH_OTHERS',
  ON_THE_PHONE = 'ON_THE_PHONE',
  OOO_VACANT = 'OOO_VACANT',
  QUIET_FOCUSED_LITTLE_INTERACTION = 'QUIET_FOCUSED_LITTLE_INTERACTION',
  WORK_AREA_TO_CALIBRATE = 'WORK_AREA_TO_CALIBRATE',
}

export interface ICalibration {
  constant: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  suggestionAcceptedByUser?: boolean;
}

export interface SuggestedCalibration extends ICalibration {
  status: string;
  isSnapped?: boolean;
}

export interface ICluster {
  floorId?: number;
  floorplanPoint: IFloorplanPoint;
  id?: number;
  name: string;
  photos: IPhoto[];
  roomName: string;
  seatCount: number;
  spaceType: ISpaceType;
  projectSpaceType?: IProjectSpaceType;
  spaceTypeId?: number;
  usf: number;
  spaceUsage?: ISpaceUsage;
  number: number;
  notes: string;
}

export interface IAdjustedFloorplanItems {
  shapes: ISpaceShape[];
  spaces: ISpace[];
  calibration: ICalibration;
  shouldDeleteCalibration?: boolean;
}
